import React, { useState, useLayoutEffect, useEffect } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { useSnackbar } from 'notistack';
import { Container, Grid, Box } from '@mui/material';
import { useForm, SubmitHandler, FormProvider } from 'react-hook-form';
import dayjs from 'dayjs';
import { Messages } from '@/common/constants';
import Loading from '@/components/common/Loading';
import WidePlanRegistry from '@/components/planRegistry/WidePlanRegistry';

// Types & Services
import PhoneService from '@/services/Phone.service';
import Phone from '@/types/Phone.type';
import ActSchedule from '@/types/ActSchedule.type';
import Style from '@/types/Style.type';
import Status from '@/types/Status.type';
import { EmployeeDepartments } from '@/types/Employee.type';
import StyleService from '@/services/Style.service';
import StatusService from '@/services/Status.service';
import ActScheduleService from '@/services/ActSchedule.service';
import SmallPlanRegistry from '@/components/planRegistry/SmallPlanRegistry';
import { QuickChangeStatusForm } from '@/components/actPlan/QuickChangeStatus';
import CompanyService from '@/services/Company.service';

const PlanRegistry: React.FC = () => {
  const [employeeDepartments, setEmployeeDepartments] = useState<EmployeeDepartments>({
    name: '',
    departments: [{ name: '', id: 0 }],
  });
  const [phones, setPhones] = useState<Array<Phone>>([]);
  const [styles, setStyles] = useState<Array<Style>>([]);
  const [statuses, setStatuses] = useState<Array<Status>>([]);
  const [loading, setLoading] = useState(false);
  const [goingStraightHomeFlag, setGoingStraightHomeFlag] = useState(false);
  const [undecidedFlag, setUndecidedFlag] = useState(false);
  const [contPlan, setContPlan] = useState(0);
  const params = useParams<{ id: string }>();
  const { enqueueSnackbar } = useSnackbar();
  const navigate = useNavigate();
  const location = useLocation();
  const [selectQuick, setSelectQuick] = useState<{ quickStatus: QuickChangeStatusForm }>(
    location.state as { quickStatus: QuickChangeStatusForm },
  );

  const useFormMethods = useForm<ActSchedule>({
    shouldUnregister: false,
  });

  const {
    handleSubmit,
    formState: { isSubmitting },
    reset,
    trigger,
    watch,
    setValue,
    getValues,
  } = useFormMethods;

  useEffect(() => {
    if (watch('undecidedFlag') === undefined) return;
    trigger('nextDate');
  }, [watch('undecidedFlag'), watch('style'), watch('nextDate')]);

  useEffect(() => {
    if (watch('goingStraightHomeFlag') === undefined) return;
    trigger('returnTime');
  }, [watch('goingStraightHomeFlag'), watch('status'), watch('returnTime')]);

  const validationRules = {
    style: {
      required: '勤務形態は必須項目です。',
    },
    startTime: {
      pattern: {
        value: new RegExp('^([01][0-9]|2[0-3]):[0-5][0-9]$'),
        message: '勤務開始時間の形式が不正です。',
      },
    },
    endTime: {
      pattern: {
        value: new RegExp('^([01][0-9]|2[0-3]):[0-5][0-9]$'),
        message: '勤務終了時間の形式が不正です。',
      },
      validate: (value: string) =>
        !value || value >= getValues('startTime')! || '勤務終了時間には、勤務開始時間以降の時間を指定してください。',
    },
    nextDate: {
      pattern: {
        value: new RegExp('^[0-9]{4}(-|/)(0[1-9]|1[0-2])(-|/)(0[1-9]|[12][0-9]|3[01])$'),
        message: '次回出社日の形式が不正です。',
      },
      validate: (value: string) =>
        !(!value && getValues('style')?.nextDateFlag && !getValues('undecidedFlag')) ||
        '選択された勤務形態は次回出社日が必要です。',
    },
    status: {
      required: 'ステータスは必須項目です。',
    },
    place: {
      maxLength: { value: 80, message: '行先は80字以内で入力してください。' },
    },
    returnTime: {
      pattern: {
        value: new RegExp('^([01][0-9]|2[0-3]):[0-5][0-9]$'),
        message: '戻り時間の形式が不正です。',
      },
      validate: (value: string) =>
        !(!value && getValues('status')?.returnTimeFlag && !getValues('goingStraightHomeFlag')) ||
        '選択されたステータスは戻り時間が必要です。',
    },
    phone: { required: '連絡先は必須項目です。' },
    goingStraightHomeFlag: {},
    undecidedFlag: {},
  };

  const handleClick30m = () => {
    if (!getValues('returnTime')) {
      if (dayjs().minute() <= 30) {
        setValue(
          'returnTime',
          dayjs()
            .add(30 - dayjs().minute(), 'minute')
            .format('HH:mm')
            .toString(),
        );
      } else {
        setValue(
          'returnTime',
          dayjs()
            .add(60 - dayjs().minute(), 'minute')
            .format('HH:mm')
            .toString(),
        );
      }
    } else {
      const [hour, minute] = getValues('returnTime')!.split(':');
      setValue(
        'returnTime',
        dayjs().set('hour', Number(hour)).set('minute', Number(minute)).add(30, 'minute').format('HH:mm').toString(),
      );
    }
  };

  const handleClick1h = () => {
    if (!getValues('returnTime')) {
      setValue(
        'returnTime',
        dayjs()
          .add(60 - dayjs().minute(), 'minute')
          .format('HH:mm')
          .toString(),
      );
    } else {
      const [hour, minute] = getValues('returnTime')!.split(':');
      setValue(
        'returnTime',
        dayjs().set('hour', Number(hour)).set('minute', Number(minute)).add(1, 'hour').format('HH:mm').toString(),
      );
    }
  };

  useLayoutEffect(() => {
    setLoading(true);
    CompanyService.get().then((response: { data: any }) => {
      setContPlan(response.data.contPlan);
    });
    Promise.all([
      ActScheduleService.get(Number(params.id)),
      StatusService.getAll(),
      StyleService.getAll(),
      PhoneService.getAll(),
    ])
      .then((responses) => {
        const [actSchedule, statuses, styles, phone] = responses;
        setPhones([{ id: 0, number: '携帯・内線' }, ...phone.data]);
        setStatuses(statuses.data);
        setStyles(styles.data);
        setEmployeeDepartments(actSchedule.data.employee);
        setUndecidedFlag(actSchedule.data.undecidedFlag);

        // PhoneIdがNullだった場合、「携帯・内線」を選択状態とさせる
        actSchedule.data.phone =
          actSchedule.data.phoneId === null ? { id: 0, number: '携帯・内線' } : actSchedule.data.phone;
        actSchedule.data.nextDate = actSchedule.data.nextDate
          ? dayjs(actSchedule.data.nextDate).format('YYYY-MM-DD')
          : '';
        actSchedule.data.endTime = actSchedule.data.endTime ?? '';
        actSchedule.data.startTime = actSchedule.data.startTime ?? '';
        actSchedule.data.place = actSchedule.data.place ?? '';
        actSchedule.data.nextDate = actSchedule.data.nextDate ?? '';
        actSchedule.data.returnTime = actSchedule.data.returnTime ?? '';
        if (selectQuick) {
          actSchedule.data.place = selectQuick.quickStatus.place ?? '';
          actSchedule.data.status = selectQuick.quickStatus;
          actSchedule.data.statusId = selectQuick.quickStatus.id;
          actSchedule.data.returnTime = '';
          actSchedule.data.goingStraightHomeFlag = false;
        }
        setGoingStraightHomeFlag(actSchedule.data.goingStraightHomeFlag);
        reset(actSchedule.data);
        setLoading(false);
      })
      .catch(() => {
        setLoading(false);
        enqueueSnackbar(Messages.select.failure);
      });
  }, []);

  const onSubmit: SubmitHandler<ActSchedule> = async (data: any) => {
    try {
      data.styleId = data.style.id;
      data.statusId = data.status.id;
      data.phoneId = data.phone.id === 0 ? null : data.phone.id;
      await ActScheduleService.updatePlanRegistry(data, Number(params.id));
      enqueueSnackbar(Messages.update.success, { variant: 'info' });
      navigate(-1);
    } catch (error: any) {
      enqueueSnackbar(error.response.data.message);
    }
  };

  if (loading) {
    return <Loading />;
  }
  return (
    <Grid container spacing={1}>
      <Grid item xs={12} sm={12} md={contPlan===1 ? 12 : 9} lg={contPlan===1 ? 12 : 9}>
        <Container component="main" maxWidth="lg">
          <FormProvider {...useFormMethods}>
            <form onSubmit={handleSubmit(onSubmit)}>
              <Box sx={{ display: { xs: 'none', sm: 'block' } }}>
                <WidePlanRegistry
                  employeeDepartments={employeeDepartments}
                  undecidedFlag={undecidedFlag}
                  goingStraightHomeFlag={goingStraightHomeFlag}
                  isSubmitting={isSubmitting}
                  validationRules={validationRules}
                  phones={phones}
                  styles={styles}
                  statuses={statuses}
                  handleClick1h={handleClick1h}
                  handleClick30m={handleClick30m}
                  setValue={setValue}
                  setUndecidedFlag={setUndecidedFlag}
                  setGoingStraightHomeFlag={setGoingStraightHomeFlag}
                  autoFocus={selectQuick ? true : false}
                />
              </Box>
              <Box sx={{ display: { xs: 'block', sm: 'none' } }}>
                <SmallPlanRegistry
                  employeeDepartments={employeeDepartments}
                  undecidedFlag={undecidedFlag}
                  goingStraightHomeFlag={goingStraightHomeFlag}
                  isSubmitting={isSubmitting}
                  validationRules={validationRules}
                  phones={phones}
                  styles={styles}
                  statuses={statuses}
                  handleClick1h={handleClick1h}
                  handleClick30m={handleClick30m}
                  setValue={setValue}
                  setUndecidedFlag={setUndecidedFlag}
                  setGoingStraightHomeFlag={setGoingStraightHomeFlag}
                  autoFocus={selectQuick ? true : false}
                />
              </Box>
            </form>
          </FormProvider>
        </Container>
      </Grid>
      {contPlan === 0 ?
        <Grid item xs={12} sm={12} md={3} lg={3}>
          <Box sx={{display: {xs: 'none', sm: 'none', md: 'none', xl: 'block'}, textAlign: 'center', marginTop: '24px'}}>
            <a href='https://si.mitani-corp.co.jp/solution/e2move/' target='_blank'>
              <img src={'/manual/336-280e2-hanbai.png'} width='100%' height='100%' style={{border: '1px solid #dcdcdc'}}/>
            </a>
          </Box>
          <Box
            sx={{display: {xs: 'none', sm: 'none', md: 'none', xl: 'block'}, textAlign: 'center', marginTop: '24px'}}>
            <a href='https://si.mitani-corp.co.jp/solution/e2move/' target='_blank'>
              <img src={'/manual/336-280e2-kouji.png'} width='100%' height='100%' style={{border: '1px solid #dcdcdc'}}/>
            </a>
          </Box>
        </Grid>
      : undefined}
    </Grid>
    
  );
};
export default PlanRegistry;
